import { MetadataFieldType } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { createUseStyles } from '@/services/createUseStyles';
import { Alert, Box, Button } from '@mui/material';
import { BulkUploadData } from '../../types';
import { MetadataBulkDataGrid } from '../MetadataBulkDataGrid';
import { getFieldTypesStepColumns } from '../columnDefinitions';

const useStyles = createUseStyles(theme => ({
  FieldTypesStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  FieldTypesStep_content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '24px',
  },
  FieldTypesStep_summary: {
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '8px',
    flexShrink: 0,
  },
  FieldTypesStep_dataGridContainer: {
    flex: 1,
    overflow: 'hidden',
    marginBottom: '24px',
  },
  FieldTypesStep_actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    position: 'sticky',
    bottom: 0,
    flexShrink: 0,
  },
}));

export const FieldTypesStep = ({
  data,
  onNext,
  onBack,
  onDataChange,
}: {
  data?: BulkUploadData;
  onNext: () => void;
  onBack: () => void;
  onDataChange: (data: BulkUploadData) => void;
}) => {
  const c = useStyles();

  if (!data) {
    return <Alert severity='error'>No data available. Please go back and upload a file.</Alert>;
  }

  const { metadataFieldDefinitions } = data;
  const newFields = metadataFieldDefinitions.filter(field => field.isNew);

  if (newFields.length === 0) {
    return (
      <Box className={c.FieldTypesStep_root}>
        <Box className={c.FieldTypesStep_content}>
          <HeaderText gutterBottom>No New Fields Detected</HeaderText>
          <Alert severity='success' sx={{ marginBottom: 2 }}>
            All metadata fields in your file already exist. You can proceed directly to asset matching.
          </Alert>
        </Box>
        <Box className={c.FieldTypesStep_actions}>
          <Button variant='outlined' onClick={onBack}>
            Back
          </Button>
          <Button variant='contained' onClick={onNext}>
            Continue
          </Button>
        </Box>
      </Box>
    );
  }

  const handleFieldTypeChange = (fieldName: string, type: MetadataFieldType) => {
    const updatedDefinitions = metadataFieldDefinitions.map(field =>
      field.name === fieldName ? { ...field, type } : field
    );

    onDataChange({
      ...data,
      metadataFieldDefinitions: updatedDefinitions,
    });
  };

  const columns = getFieldTypesStepColumns(data, handleFieldTypeChange);

  const rows = newFields.map((field, index) => ({
    id: `field-${index}`,
    ...field,
  }));

  return (
    <Box className={c.FieldTypesStep_root}>
      <Box className={c.FieldTypesStep_content}>
        <HeaderText gutterBottom>Configure New Metadata Fields</HeaderText>
        <Box className={c.FieldTypesStep_summary}>
          <HeaderText gutterBottom>New Fields Detected</HeaderText>
          <BodyText>
            Your file contains {newFields.length} metadata field{newFields.length === 1 ? '' : 's'} that{' '}
            {newFields.length === 1 ? `doesn't` : `don't`} exist yet. Please specify the data type for each field.
          </BodyText>
        </Box>
        <Alert severity='info' sx={{ marginBottom: 2 }}>
          <BodyText>
            <strong>Field Types:</strong>
            <br />• <strong>Text:</strong> For strings, names, descriptions, etc.
            <br />• <strong>Number:</strong> For numeric values, quantities, measurements, etc.
            <br />• <strong>Boolean:</strong> For true/false values, flags, etc.
          </BodyText>
        </Alert>
        <Box className={c.FieldTypesStep_dataGridContainer}>
          <MetadataBulkDataGrid columns={columns} rows={rows} rowCount={rows.length} />
        </Box>
      </Box>
      <Box className={c.FieldTypesStep_actions}>
        <Button variant='outlined' onClick={onBack}>
          Back
        </Button>
        <Button variant='contained' onClick={onNext}>
          Continue
        </Button>
      </Box>
    </Box>
  );
};
